import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { CreditCard, Phone, User, CheckCircle, XCircle, Clock, ArrowLeft, Loader2, Tag } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Progress } from '../components/ui/Progress';
import { Separator } from '../components/ui/Separator';
import { Badge } from '../components/ui/Badge';
import { useLanguage } from '../contexts/LanguageContext';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { Footer } from '../components/Footer';
import { toast } from 'sonner';
type Step = 'info' | 'instructions' | 'verify' | 'pending' | 'result';
export function PaymentFlow() {
  const {
    fileId
  } = useParams<{
    fileId: string;
  }>();
  const {
    t
  } = useLanguage();
  const {
    files,
    addPaymentRequest,
    getPaymentStatus,
    payments,
    validateDiscount,
    useDiscountCode
  } = useData();
  const {
    currentUser
  } = useAuth();
  const navigate = useNavigate();
  const file = files.find(f => f.id === fileId);
  const [step, setStep] = useState<Step>('info');
  const [payerPhone, setPayerPhone] = useState('');
  const [payerName, setPayerName] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Discount code state
  const [discountInput, setDiscountInput] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState<{
    code: string;
    percentage: number;
  } | null>(null);
  const existingPayment = currentUser && fileId ? getPaymentStatus(currentUser.id, fileId) : undefined;
  useEffect(() => {
    if (existingPayment) {
      if (existingPayment.status === 'pending') setStep('pending');else if (existingPayment.status === 'verified') setStep('result');
    }
  }, [existingPayment]);
  useEffect(() => {
    if (step !== 'pending') return;
    if (!currentUser || !fileId) return;
    const payment = payments.find((p: any) => p.userId === currentUser.id && p.fileId === fileId);
    if (payment && payment.status !== 'pending') setStep('result');
  }, [step, currentUser, fileId, payments]);
  if (!file) {
    return <div className="flex-1 flex items-center justify-center p-8" data-id="element-928">
        <p className="text-muted-foreground" data-id="element-929">File not found</p>
      </div>;
  }
  if (!currentUser) {
    navigate('/auth');
    return null;
  }
  const finalPrice = appliedDiscount ? Math.round(file.price * (1 - appliedDiscount.percentage / 100)) : file.price;
  const handleApplyDiscount = () => {
    if (!discountInput.trim()) return;
    const discount = validateDiscount(discountInput.trim());
    if (discount) {
      setAppliedDiscount({
        code: discount.code,
        percentage: discount.percentage
      });
      toast.success(`Discount applied! ${discount.percentage}% off`);
    } else {
      toast.error('Invalid or expired discount code');
      setAppliedDiscount(null);
    }
  };
  const handleSubmitVerification = async () => {
    const errs: Record<string, string> = {};
    if (!payerPhone || payerPhone.replace(/\D/g, '').length !== 10) errs.phone = t('auth.error.phone');
    if (!payerName.trim()) errs.name = t('auth.error.required');
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    addPaymentRequest({
      userId: currentUser.id,
      userName: currentUser.fullName,
      userPhone: currentUser.countryCode + currentUser.phone,
      fileId: file.id,
      fileName: file.name,
      amount: finalPrice,
      payerMtnNumber: payerPhone.replace(/\D/g, ''),
      payerName: payerName.trim(),
      ...(appliedDiscount ? {
        discountCode: appliedDiscount.code,
        originalAmount: file.price
      } : {})
    });
    if (appliedDiscount) {
      await useDiscountCode(appliedDiscount.code);
    }
    toast.success(t('payment.submit'));
    setStep('pending');
  };
  const currentPayment = payments.find(p => p.userId === currentUser.id && p.fileId === fileId);
  const isVerified = currentPayment?.status === 'verified';
  const isDenied = currentPayment?.status === 'denied';
  const stepProgress = {
    info: 25,
    instructions: 50,
    verify: 75,
    pending: 90,
    result: 100
  };
  return <div className="w-full min-h-screen flex flex-col" data-id="element-930">
      <div className="flex-1 py-8 px-4" data-id="element-931">
        <div className="mx-auto max-w-lg" data-id="element-932">
          <Button variant="ghost" size="sm" className="mb-4 gap-2" onClick={() => navigate('/marketplace')} data-id="element-933">
            <ArrowLeft className="h-4 w-4" data-id="element-934" /> {t('common.back')}
          </Button>
          <Progress value={stepProgress[step]} className="mb-6 h-2" data-id="element-935" />

          <AnimatePresence mode="wait" data-id="element-936">
            {/* Step 1: File Info */}
            {step === 'info' && <motion.div key="info" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} data-id="element-937">
                <Card data-id="element-938">
                  <CardHeader data-id="element-939">
                    <CardTitle className="flex items-center gap-2" data-id="element-940">
                      <CreditCard className="h-5 w-5 text-blue-600" data-id="element-941" />
                      {t('payment.title')}
                    </CardTitle>
                    <CardDescription data-id="element-942">{file.name}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4" data-id="element-943">
                    <div className="aspect-video rounded-lg overflow-hidden bg-muted" data-id="element-944">
                      <img src={file.coverUrl} alt={file.name} className="w-full h-full object-cover" data-id="element-945" />
                    </div>
                    <p className="text-sm text-muted-foreground" data-id="element-946">
                      {file.description}
                    </p>
                    <Separator data-id="element-947" />

                    {/* Discount Code */}
                    <div className="space-y-2" data-id="element-948">
                      <Label className="flex items-center gap-1.5 text-xs" data-id="element-949">
                        <Tag className="h-3.5 w-3.5" data-id="element-950" /> Have a discount code?
                      </Label>
                      <div className="flex gap-2" data-id="element-951">
                        <Input value={discountInput} onChange={e => setDiscountInput(e.target.value.toUpperCase())} placeholder="Enter code" className="flex-1 text-sm" data-id="element-952" />
                        <Button size="sm" variant="outline" onClick={handleApplyDiscount} className="text-xs" data-id="element-953">
                          Apply
                        </Button>
                      </div>
                      {appliedDiscount && <motion.div initial={{
                    opacity: 0,
                    y: -5
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} data-id="element-954">
                          <Badge className="bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 gap-1" data-id="element-955">
                            <CheckCircle className="h-3 w-3" data-id="element-956" />
                            {appliedDiscount.code}: {appliedDiscount.percentage}
                            % off!
                          </Badge>
                        </motion.div>}
                    </div>

                    <div className="flex items-center justify-between" data-id="element-957">
                      <span className="text-sm font-medium" data-id="element-958">
                        {t('payment.amount')}:
                      </span>
                      <div className="text-right" data-id="element-959">
                        {appliedDiscount ? <div data-id="element-960">
                            <span className="text-sm text-muted-foreground line-through mr-2" data-id="element-961">
                              {file.price.toLocaleString()} FRW
                            </span>
                            <span className="text-2xl font-bold text-green-600" data-id="element-962">
                              {finalPrice.toLocaleString()} FRW
                            </span>
                          </div> : <span className="text-2xl font-bold text-blue-600" data-id="element-963">
                            {file.price.toLocaleString()} FRW
                          </span>}
                      </div>
                    </div>
                    <Button className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold gap-2" onClick={() => setStep('instructions')} data-id="element-964">
                      <CreditCard className="h-4 w-4" data-id="element-965" />
                      {t('payment.method')}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>}

            {/* Step 2: Instructions */}
            {step === 'instructions' && <motion.div key="instructions" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} data-id="element-966">
                <Card data-id="element-967">
                  <CardHeader data-id="element-968">
                    <CardTitle data-id="element-969">{t('payment.instructions')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4" data-id="element-970">
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 space-y-3" data-id="element-971">
                      <div className="flex items-center justify-between" data-id="element-972">
                        <span className="text-sm text-muted-foreground" data-id="element-973">
                          {t('payment.amount')}:
                        </span>
                        <span className="font-bold text-lg" data-id="element-974">
                          {finalPrice.toLocaleString()} FRW
                        </span>
                      </div>
                      {appliedDiscount && <div className="flex items-center justify-between" data-id="element-975">
                          <span className="text-sm text-muted-foreground" data-id="element-976">
                            Discount:
                          </span>
                          <Badge className="bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300" data-id="element-977">
                            {appliedDiscount.code} (-
                            {appliedDiscount.percentage}%)
                          </Badge>
                        </div>}
                      <Separator data-id="element-978" />
                      <div className="flex items-center justify-between" data-id="element-979">
                        <span className="text-sm text-muted-foreground" data-id="element-980">
                          {t('payment.mtn.number')}:
                        </span>
                        <span className="font-mono font-bold text-lg" data-id="element-981">
                          0783138128
                        </span>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-982">
                        <span className="text-sm text-muted-foreground" data-id="element-983">
                          {t('payment.mtn.name')}:
                        </span>
                        <span className="font-semibold" data-id="element-984">BAYAVUGE THEOGEN</span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground text-center" data-id="element-985">
                      MTN Mobile Money → *182*1*1# → Enter number → Confirm
                    </p>
                    <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-2" onClick={() => setStep('verify')} data-id="element-986">
                      {t('payment.verify.title')}{' '}
                      <ArrowLeft className="h-4 w-4 rotate-180" data-id="element-987" />
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>}

            {/* Step 3: Verify */}
            {step === 'verify' && <motion.div key="verify" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} data-id="element-988">
                <Card data-id="element-989">
                  <CardHeader data-id="element-990">
                    <CardTitle data-id="element-991">{t('payment.verify.title')}</CardTitle>
                    <CardDescription data-id="element-992">
                      {file.name} — {finalPrice.toLocaleString()} FRW
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4" data-id="element-993">
                    <div className="space-y-2" data-id="element-994">
                      <Label className="flex items-center gap-2" data-id="element-995">
                        <Phone className="h-3.5 w-3.5" data-id="element-996" />{' '}
                        {t('payment.verify.phone')}
                      </Label>
                      <Input type="tel" placeholder="07XXXXXXXX" value={payerPhone} onChange={e => setPayerPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} className={errors.phone ? 'border-red-500' : ''} data-id="element-997" />
                      {errors.phone && <p className="text-xs text-red-500" data-id="element-998">{errors.phone}</p>}
                    </div>
                    <div className="space-y-2" data-id="element-999">
                      <Label className="flex items-center gap-2" data-id="element-1000">
                        <User className="h-3.5 w-3.5" data-id="element-1001" />{' '}
                        {t('payment.verify.name')}
                      </Label>
                      <Input placeholder="Full name on MTN account" value={payerName} onChange={e => setPayerName(e.target.value)} className={errors.name ? 'border-red-500' : ''} data-id="element-1002" />
                      {errors.name && <p className="text-xs text-red-500" data-id="element-1003">{errors.name}</p>}
                    </div>
                    <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-2" onClick={handleSubmitVerification} data-id="element-1004">
                      {t('payment.submit')}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>}

            {/* Step 4: Pending */}
            {step === 'pending' && <motion.div key="pending" initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} data-id="element-1005">
                <Card data-id="element-1006">
                  <CardContent className="py-12 text-center space-y-4" data-id="element-1007">
                    <motion.div animate={{
                  rotate: 360
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'linear'
                }} className="mx-auto w-fit" data-id="element-1008">
                      <Loader2 className="h-12 w-12 text-blue-600" data-id="element-1009" />
                    </motion.div>
                    <h3 className="font-heading text-xl font-bold" data-id="element-1010">
                      {t('payment.pending')}
                    </h3>
                    <p className="text-sm text-muted-foreground max-w-sm mx-auto" data-id="element-1011">
                      {t('payment.pending.msg')}
                    </p>
                    <Badge variant="secondary" className="gap-1" data-id="element-1012">
                      <Clock className="h-3 w-3" data-id="element-1013" /> Pending
                    </Badge>
                    <Separator data-id="element-1014" />
                    <Button variant="outline" onClick={() => navigate('/marketplace')} className="gap-2" data-id="element-1015">
                      <ArrowLeft className="h-4 w-4" data-id="element-1016" /> {t('common.back')}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>}

            {/* Step 5: Result */}
            {step === 'result' && <motion.div key="result" initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} data-id="element-1017">
                <Card data-id="element-1018">
                  <CardContent className="py-12 text-center space-y-4" data-id="element-1019">
                    {isVerified ? <>
                        <motion.div initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} transition={{
                    type: 'spring'
                  }} data-id="element-1020">
                          <CheckCircle className="h-16 w-16 text-green-600 mx-auto" data-id="element-1021" />
                        </motion.div>
                        <h3 className="font-heading text-xl font-bold text-green-600" data-id="element-1022">
                          {t('payment.verified')}
                        </h3>
                        <p className="text-sm text-muted-foreground" data-id="element-1023">
                          You can now download {file.name}
                        </p>
                        <Button className="bg-green-600 hover:bg-green-700 text-white gap-2" onClick={() => navigate('/profile')} data-id="element-1024">
                          Go to My Downloads
                        </Button>
                      </> : isDenied ? <>
                        <motion.div initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} transition={{
                    type: 'spring'
                  }} data-id="element-1025">
                          <XCircle className="h-16 w-16 text-red-500 mx-auto" data-id="element-1026" />
                        </motion.div>
                        <h3 className="font-heading text-xl font-bold text-red-500" data-id="element-1027">
                          {t('payment.denied')}
                        </h3>
                        <p className="text-sm text-muted-foreground" data-id="element-1028">
                          {t('payment.denied.msg')}
                        </p>
                        <Button variant="outline" onClick={() => setStep('info')} className="gap-2" data-id="element-1029">
                          Try Again
                        </Button>
                      </> : <>
                        <Clock className="h-16 w-16 text-yellow-500 mx-auto" data-id="element-1030" />
                        <h3 className="font-heading text-xl font-bold" data-id="element-1031">
                          {t('payment.pending')}
                        </h3>
                        <p className="text-sm text-muted-foreground" data-id="element-1032">
                          {t('payment.pending.msg')}
                        </p>
                      </>}
                  </CardContent>
                </Card>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
      <Footer data-id="element-1033" />
    </div>;
}